import { useNavigate } from "react-router-dom";
import useDocumentTitle from "../../util/useDocumentTitle";
import { useGetMarinerEvaluationInsightsQuery } from "../../redux/api/evaluation";
import EvaluationInsightTable from "./EvaluationInsightsTable";
import { Dispatch } from "../../common/Dispatch";
import { selectCurrentDispatch, selectCurrentUser } from "../../redux/authentication/authSlice";
import { useSelector } from "react-redux";
import { useGetEvaluateeDispatchesQuery } from "../../redux/api/dispatch";
import { Autocomplete, Modal, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { useCreateEvaluationFromDispatchMutation } from "../../redux/api/evaluation";
import { EvaluationInsightView, EvaluationStatus } from "../../common/Evaluation";
import { toast } from "react-toastify";
import MatrixModal from "./MatrixModal";
import { JobRating } from "../../common/JobOrder";
import { UserGroup } from "../../common/User";
import { Fleet } from "../../common/Fleet";
import { Vessel } from "../../common/Vessel";
import { useGetFleetsQuery } from "../../redux/api/fleet";


const MarinerEvaluationInsights = () => {
  useDocumentTitle("Performance Evaluations");
  const navigate = useNavigate();
  const currentDispatch: Dispatch = useSelector(selectCurrentDispatch);
  const isArc = currentDispatch?.vessel?.name ? currentDispatch?.vessel?.name.startsWith('ARC') : false;
  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [upcomingEvals, setUpcomingEvals] = useState<EvaluationInsightView[]>([]);

  const { data: fleets, isLoading: isFleetsLoading } = useGetFleetsQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
        skip: !(userGroup === UserGroup.FLEET_EXECS || userGroup === UserGroup.VESSEL_EXECS )
      }
    );

  const { data: evaluationInsights, isLoading: isEvaluationInsightsLoading } =
    useGetMarinerEvaluationInsightsQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      }
    );
  const {
    data: dispatches,
    isLoading: isDispatchesLoading,
  } = useGetEvaluateeDispatchesQuery( 
      {},
      {
        refetchOnMountOrArgChange: true,
        skip: !isAssessor(),
      }
  );

  useEffect(() => {
    if(vessel !== null){
      setUpcomingEvals(evaluationInsights?.activeEvaluations.filter((insight) => insight.vessel.id === vessel.id) ?? [])
    } else if(fleet !== null){
      setUpcomingEvals(evaluationInsights?.activeEvaluations.filter((insight) => fleet.vessels.some( vessel => vessel.id === insight.vessel?.id)) ?? [])
    } else {
      setUpcomingEvals(evaluationInsights?.activeEvaluations ?? [])
    }
  }, [fleet, vessel, evaluationInsights]);

  const [openModal, setOpenModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<number | null>(null);
  const [createEvalFromDispatch] = useCreateEvaluationFromDispatchMutation();
  const [isCreating, setIsCreating] = useState(false);

  const handleClick = async (evaluationId: number | null, dispatchId?: number) => {
    setIsCreating(true);
    if (evaluationId) {
      navigate(`/evaluations/${evaluationId}`);
      setIsCreating(false);
      return;
    }

    if (!dispatchId) {
      toast.error(<p>Missing dispatch ID for evaluation creation.</p>, {
        position: "top-center",
      });
      setIsCreating(false);
      return;
    }

    try {
      const result = await createEvalFromDispatch({ dispatchId }).unwrap();
      navigate(`/evaluations/${result.id}`);
    } catch (error) {
      console.error("Error creating evaluation:", error);
      toast.error(<p>Failed to create evaluation. Please try again.</p>, {
        position: "top-center",
      });
    }
    setIsCreating(false);
  };

  const getReminderEmail = (insight: EvaluationInsightView) => {
    switch (insight.status) {
      case EvaluationStatus.EVALUATION_INITIATED:
      case EvaluationStatus.ASSESSOR_1_EDIT:
      case EvaluationStatus.ASSESSOR_2_RETURN:
        return insight.assessor1Email;
      case EvaluationStatus.ASSESSOR_1_SUBMIT:
      case EvaluationStatus.FINAL_REVIEWER_RETURN:
        return insight.assessor2Email;
      case EvaluationStatus.ASSESSOR_2_APPROVE:
        return insight.finalReviewerEmail;
      case EvaluationStatus.FINAL_REVIEWER_APPROVE:
        return insight.profile.email;
      default:
        return "";
    }
  };

  function isAssessor(): boolean {
    if (!currentDispatch) return false;
    const assessorRatings = ["_MASTER", "_CM", "_CE", "_STW", "_STB", "_1AE"];
    return assessorRatings.includes(currentDispatch.rating.toUpperCase());
  }

  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  return (
    <>
      {!isArc ? (<div className="flex-col wd-100">
        <div className="roster-page-header flex justify-content-btwn align-items-center">
          <h1>Performance Evaluations</h1>
          {isAssessor() && currentDispatch && !isDispatchesLoading && (
            <div className="flex align-items-center p-2">
              <MatrixModal
                evaluationType="all"
                hasRos={currentDispatch?.vessel?.ros === true}
                tortuga={currentDispatch?.vessel?.fleet?.name === 'TORTUGA'}
              />
              <Button
                variant="contained"
                className="blue-button"
                onClick={() => setOpenModal(true)}
              >
                + New Evaluation
              </Button>
            </div>
          )}
        </div>

        {currentDispatch && (
          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            aria-labelledby="new-evaluation-modal"
            aria-describedby="create-new-evaluation"
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "8px",
                width: "400px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              }}
            >
              <h2 id="new-evaluation-modal">Create New Evaluation</h2>
              <div style={{ marginTop: "10px" }}>
                <label className="required">Select Crew Member</label>
                <select
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px",
                  }}
                  defaultValue=""
                  onChange={(e) => setSelectedDispatch(Number(e.target.value))}
                >
                  <option value="" disabled>
                    Choose a crew member...
                  </option>
                  {dispatches
                    ?.filter(d => ![JobRating._Master, JobRating._CM, JobRating._CE, JobRating._1AE].includes(d?.rating))
                    .map((d: any) => {
                      const profile = d.profile ?? {};
                      const fullName = [
                        profile.firstName,
                        profile.middleName,
                        profile.lastName,
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <option key={d.id} value={d.id}>
                          {`${fullName || ""} - ${d?.rating ?? ""}`}
                        </option>
                      );
                    })
                  }
                </select>
              </div>
              <div className="flex justify-content-end mt-3">
                <Button
                  variant="contained"
                  className="blue-button"
                  disabled={isCreating}
                  onClick={async () => {
                    if (!selectedDispatch) {
                      alert("Please select a crew member first.");
                      return;
                    }
                    try {
                      setIsCreating(true);
                      const result = await createEvalFromDispatch({
                        dispatchId: selectedDispatch,
                      }).unwrap();
                      setOpenModal(false);
                      navigate(`/evaluations/${result.id}`);
                    } catch (error) {
                      console.error("Error creating evaluation:", error);
                      alert("Failed to create evaluation. Please try again.");
                    } finally {
                      setIsCreating(false);
                    }
                  }}
                >
                  {isCreating ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          </Modal>
        )}
        {!isEvaluationInsightsLoading && !!evaluationInsights && (
          <>
            <h2 style={{ color: "black" }}>Evaluations Needing Action</h2>
            <EvaluationInsightTable
              evalList={evaluationInsights.evalsNeedingAction}
              handleClick={handleClick}
            />
            <div style={{ marginTop: "40px" }}></div>

            {isAssessor() && currentDispatch && (
              <>
                <div>
                  <h2 style={{ color: "black" }}>Active Evaluations</h2>
                </div>
                <EvaluationInsightTable
                  evalList={evaluationInsights.activeEvaluations}
                  getReminderEmail={getReminderEmail}
                  emailCallback={handleEmailCallBack}
                  handleClick={handleClick}
                  activeEval
                />
                <div style={{ marginTop: "40px" }}></div>
                <div>
                  <h2 style={{ color: "black" }}>Offboarding Soon</h2>
                </div>
                <EvaluationInsightTable
                  evalList={evaluationInsights.offboardingSoon}
                  getReminderEmail={getReminderEmail}
                  emailCallback={handleEmailCallBack}
                  handleClick={handleClick}
                />
              </>
            )}
            {(userGroup === UserGroup.FLEET_EXECS ||
              userGroup === UserGroup.VESSEL_EXECS) &&
              <>
                <div>
                  <h2 style={{ color: "black" }}>Upcoming Evaluations</h2>
                </div>
              <div className="eval-insights-sticky eval-fleet">
                {!isFleetsLoading && (
                  <>
                    <div className="flex align-items-center g-3">
                      <label className="large-filter mb-2" style={{ width: "250px" }}>
                        <span>Fleet:</span>
                        <Autocomplete
                          className="fleet-dropdown"
                          // ARC shouldn't be available in fleet dropdown
                          options={
                            fleets
                              ? (fleets as Fleet[]).filter(
                                (fleet) => fleet.name !== "ARC"
                              )
                              : []
                          }
                          value={fleet}
                          onChange={(_, value) => {
                            setFleet(value);
                            setVessel(null);
                          }}
                          renderInput={(params) => <TextField {...params} />}
                          isOptionEqualToValue={(opt, val) => opt.id === val.id}
                          getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                        />
                      </label>
                      {!!fleet && (
                        <label className="large-filter mb-2" style={{ width: "250px" }}>
                          <span>Vessel:</span>
                          <Autocomplete
                            className="vessel-dropdown"
                            options={fleet?.vessels || []}
                            value={vessel}
                            onChange={(_, value) => {
                              setVessel(value);
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                          />
                        </label>
                      )}
                    </div>
                    <EvaluationInsightTable
                      evalList={upcomingEvals}
                      getReminderEmail={getReminderEmail}
                      emailCallback={handleEmailCallBack}
                      handleClick={handleClick}
                      activeEval
                    />
                  </>
                )}
              </div>
              </>
            }
          </>
        )}
      </div>) : 
      <>
        <div className="roster-page-header flex justify-content-btwn align-items-center">
            <h1>Performance Evaluations</h1>
        </div>
        <div>Evaluations for ARC Vessels are not done through MSMT.</div>
      </>}
    </>
  );
};

export default MarinerEvaluationInsights;
